import { overwriteGetLocale, overwriteSetLocale } from "@repo/i18n/runtime";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { createTranslator } from "./catalogue";
import { toUserMessage } from "./error-message";
import { resolveLocale } from "./locale";
import { createLocaleStore, } from "./locale-store";
/**
 * Build the hooks against one platform. The app calls this ONCE, at module
 * scope, and exports what it returns: a second call is a second store, and two
 * stores disagree about the language the moment either one is written.
 *
 * Tests build their own store with `createLocaleStore` and a memory
 * persistence, and never need this.
 */
export function createI18n(platform) {
    const localeStore = createLocaleStore(null, {
        persistence: platform.persistence,
        onPersistError: platform.onPersistError,
    });
    /** What renders until `hydrate` answers. */
    const deviceLocale = () => resolveLocale(platform.preferredLocales());
    /**
     * ── PARAGLIDE'S RUNTIME READS THIS STORE, SYNCHRONOUSLY. ────────────────
     * Screens call compiled messages directly (`m["today.greeting"]()`), and
     * those ask the runtime's `getLocale()` for the language. Overwriting it
     * here makes the store the only source: the stored field once hydrated, the
     * device resolution before — the same value `useLocale` renders, from the
     * first frame. The compiled strategy list (cookie, global variable, base
     * locale) is never consulted after this line.
     *
     * It is `overwriteGetLocale`, not a `defineCustomClientStrategy`, on
     * purpose: an async custom strategy is silently SKIPPED by the synchronous
     * `getLocale()`, and this needs no compile option in `@repo/i18n`.
     *
     * `setLocale` is overwritten too, so a caller that reaches for Paraglide's
     * own setter writes the user's field (persisted, notified) instead of a
     * cookie nothing reads, and never reloads the page.
     */
    overwriteGetLocale(() => localeStore.getSnapshot() ?? deviceLocale());
    overwriteSetLocale((locale) => {
        localeStore.set(locale);
    });
    /**
     * The active locale: the stored field, which the user owns and which beats
     * the operating system (see `locale-store.ts`).
     *
     * Until `hydrate` answers there is nothing to render but the device
     * resolution, because reading storage is asynchronous. Once the field
     * exists, an OS language change moves nothing here.
     *
     * ── THAT WINDOW IS VISIBLE TO EXACTLY THE USER THIS FEATURE IS FOR. ───────
     * For someone who never overrode anything, the device resolution IS what
     * hydration is about to return, and nothing flickers. For someone who chose
     * English on a Portuguese phone — the case the whole setting exists to serve
     * — the first frames of a cold start render Portuguese and then flip to
     * English. It is brief and cosmetic, and it is named here rather than
     * engineered around, so that whoever eventually sees it on a device finds it
     * described instead of denied.
     */
    function useLocale() {
        // The store's own bound methods, never a fresh arrow wrapping them: a new
        // identity per render makes React resubscribe on every render.
        const stored = useSyncExternalStore(localeStore.subscribe, localeStore.getSnapshot, localeStore.getSnapshot);
        useEffect(() => {
            // `hydrate` is idempotent and resolves even when storage is unavailable,
            // so there is no error path to handle here; what it reports it reports
            // through `onPersistError`.
            localeStore.hydrate(platform.preferredLocales());
        }, []);
        return useMemo(() => stored ?? deviceLocale(), [stored]);
    }
    /**
     * The translator for the active locale.
     *
     * Memoized on the locale so the identity is stable between language changes
     * — a translator passed into a memoized child should not invalidate it on
     * every render.
     */
    function useTranslate() {
        const locale = useLocale();
        return useMemo(() => createTranslator(locale), [locale]);
    }
    /** Turn anything caught in a screen into a sentence in the active locale. */
    function useErrorMessage() {
        const translate = useTranslate();
        return useCallback((error) => toUserMessage(translate, error), [translate]);
    }
    return { localeStore, useErrorMessage, useLocale, useTranslate };
}
//# sourceMappingURL=use-i18n.js.map